'use client';

import { useEffect, useState, useRef } from 'react';

// 数字滚动计数器
function AnimatedCounter({ target, duration = 2000, className = "" }: { target: number; duration?: number; className?: string }) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 如果 target > 0 且还未动画，立即开始
    if (target > 0 && !hasAnimated.current) {
      hasAnimated.current = true;
      let startTime: number;
      
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        const easeOut = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(easeOut * target));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    } else if (target === 0) {
      setCount(0);
    }
  }, [target, duration]);

  return <div ref={ref} className={className}>{count}</div>;
}

// 散点粒子背景
function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    // 检测移动端
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // 减少移动端粒子数
      setShouldAnimate(true);
    };
    checkMobile();
    
    const updateDimensions = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0 || !shouldAnimate) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    // 创建粒子 - 移动端减少粒子数
    const particles: {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
      color: string;
      twinkleSpeed: number;
      twinklePhase: number;
    }[] = [];

    const colors = ['#22d3ee', '#a855f7', '#f97316', '#10b981', '#3b82f6'];
    const particleCount = isMobile ? 40 : 100; // 移动端40个，桌面100个

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * dimensions.width,
        y: Math.random() * dimensions.height,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        size: Math.random() * 1.5 + 0.5,
        opacity: Math.random() * 0.4 + 0.2,
        color: colors[Math.floor(Math.random() * colors.length)],
        twinkleSpeed: Math.random() * 0.015 + 0.005,
        twinklePhase: Math.random() * Math.PI * 2,
      });
    }

    let animationId: number;
    let frame = 0;
    const skipFrames = isMobile ? 2 : 1; // 移动端隔帧渲染

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;

      particles.forEach((p) => {
        // 更新位置 - 移动端慢一点
        p.x += p.vx;
        p.y += p.vy;

        // 边界反弹
        if (p.x < 0 || p.x > dimensions.width) p.vx *= -1;
        if (p.y < 0 || p.y > dimensions.height) p.vy *= -1;

        // 闪烁效果
        const twinkle = Math.sin(frame * p.twinkleSpeed + p.twinklePhase) * 0.3 + 0.7;
        const currentOpacity = p.opacity * twinkle;

        // 绘制粒子
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = currentOpacity;
        ctx.fill();

        // 移动端跳过光晕绘制
        if (!isMobile) {
          // 绘制光晕
          const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
          gradient.addColorStop(0, p.color);
          gradient.addColorStop(1, 'transparent');
          ctx.globalAlpha = currentOpacity * 0.3;
          ctx.fillStyle = gradient;
          ctx.fill();
        }
      });

      // 移动端不绘制连线
      if (!isMobile && frame % skipFrames === 0) {
        // 绘制连线（只连接距离较近的粒子）
        ctx.globalAlpha = 0.08;
        ctx.strokeStyle = '#22d3ee';
        ctx.lineWidth = 0.5;
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 100) {
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.stroke();
            }
          }
        }
      }

      ctx.globalAlpha = 1;
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationId);
  }, [dimensions, isMobile, shouldAnimate]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      style={{ opacity: isMobile ? 0.5 : 0.7 }}
    />
  );
}

// 鼠标跟随光点
function MouseGlow() {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      if (!isVisible) setIsVisible(true);
    };
    
    const handleMouseLeave = () => setIsVisible(false);
    
    window.addEventListener('mousemove', handleMouseMove);
    document.body.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isVisible]);
  
  return (
    <div
      className={`fixed pointer-events-none z-50 w-64 h-64 rounded-full transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      style={{
        transform: `translate(${position.x - 128}px, ${position.y - 128}px)`,
        background: 'radial-gradient(circle, rgba(34,211,238,0.12) 0%, rgba(168,85,247,0.08) 50%, transparent 70%)',
      }}
    />
  );
}

export { AnimatedCounter, ParticleBackground, MouseGlow };
